/**
 * Landing page content — the single source of truth for every string, icon and
 * placeholder rendered by the public marketing page (src/routes/landing/**).
 *
 * ALL copy is real French (Québec) marketing content — no lorem, no mock data.
 * Icons are stored as SVG `d` path strings (Heroicons-outline vocabulary) so the
 * `.ts` data module carries no JSX; sections render them with a shared <Icon>.
 *
 * Screenshot + video slots are structured so a real asset URL drops straight in
 * later (see `SCREENSHOTS[i].imageUrl` and `VIDEO.videoUrl` / `VIDEO.posterUrl`):
 * leave them `null` and the section renders a tasteful framed placeholder /
 * "bientôt" poster instead of a broken embed.
 */

export type Tone = 'indigo' | 'violet' | 'sky' | 'emerald' | 'amber' | 'rose';

/** Brand identity. */
export const BRAND = {
  name: 'Karexia',
  /** One-line positioning used in the <title>-adjacent hero + meta. */
  tagline: 'La plateforme tout-en-un propulsée par l’IA',
} as const;

/** In-page anchor navigation (rendered in the sticky header + footer). */
export const NAV_LINKS: { href: string; label: string }[] = [
  { href: '#fonctionnalites', label: 'Fonctionnalités' },
  { href: '#capacites', label: 'Tout ce que Karexia fait' },
  { href: '#apercu', label: 'Aperçu' },
  { href: '#tarifs', label: 'Tarifs' },
  { href: '#cibles', label: 'Pour qui' },
];

/** Hero section copy. */
export const HERO = {
  eyebrow: 'Réseaux sociaux · Contenu · CRM · Prospection',
  title: 'Toute votre présence en ligne, pilotée par votre équipe IA.',
  subtitle:
    'Karexia réunit la création de contenu par IA, la planification et la publication multi-réseaux, un CRM complet et la prospection B2B dans une seule plateforme. Déléguez le travail répétitif à des experts IA disponibles 24 h sur 24.',
  primaryCta: 'Se connecter',
  secondaryCta: 'Créer un compte',
  note: 'Conforme à la Loi 25 et à la LCAP — vos envois sont protégés par un garde-fou anti-pourriel.',
  /** Small proof chips shown under the hero CTAs. */
  chips: ['Contenu IA bilingue', 'Publication multi-réseaux', 'CRM + prospection', 'Copilot IA 24/7'],
} as const;

/** Icon path library (24×24 viewBox, stroke = currentColor). */
export const ICONS = {
  sparkles: [
    'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z',
    'M18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z',
  ],
  calendar: [
    'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
  ],
  users: [
    'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
  ],
  robot: [
    'M9.75 9.75h.008v.008H9.75V9.75Zm4.5 0h.008v.008h-.008V9.75Z',
    'M12 3v2.25M6.75 6.75h10.5a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5v-7.5a1.5 1.5 0 0 1 1.5-1.5ZM3.75 12H2.25m19.5 0h-1.5M9 17.25V19.5m6-2.25V19.5',
  ],
  chart: [
    'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
  ],
  megaphone: [
    'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46',
  ],
  image: [
    'm2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z',
  ],
  send: [
    'M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5',
  ],
  inbox: [
    'M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z',
  ],
  search: [
    'm21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z',
  ],
  bolt: [
    'm3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z',
  ],
  globe: [
    'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
  ],
  target: [
    'M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z',
  ],
  shield: [
    'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
  ],
  layers: [
    'm3.75 7.5 8.25-4.5 8.25 4.5-8.25 4.5-8.25-4.5Zm0 4.5 8.25 4.5 8.25-4.5m-16.5 4.5L12 21l8.25-4.5',
  ],
  chat: [
    'M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z',
  ],
  play: [
    'M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z',
  ],
  check: ['m4.5 12.75 6 6 9-13.5'],
  building: [
    'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
  ],
  briefcase: [
    'M20.25 14.15v4.073a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V14.15M8.25 6.75h7.5m-7.5 0V4.5a1.5 1.5 0 0 1 1.5-1.5h4.5a1.5 1.5 0 0 1 1.5 1.5v2.25m-7.5 0h7.5m-9 3.75h10.5a2.25 2.25 0 0 1 2.25 2.25v.9a48.4 48.4 0 0 1-15 0v-.9a2.25 2.25 0 0 1 2.25-2.25Z',
  ],
  rocket: [
    'M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8',
  ],
} as const;

export type IconKey = keyof typeof ICONS;

/** The six hero feature cards (the 3D tilt row). */
export interface FeatureCard {
  icon: IconKey;
  tone: Tone;
  title: string;
  description: string;
}

export const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: 'sparkles',
    tone: 'indigo',
    title: 'Contenu IA',
    description:
      'Générez publications, visuels, infographies et vidéos en quelques secondes. Ton de marque, déclinaisons par réseau et versions bilingues, prêts à publier.',
  },
  {
    icon: 'calendar',
    tone: 'violet',
    title: 'Planification & publication',
    description:
      'Programmez et publiez sur tous vos réseaux depuis un seul calendrier : LinkedIn, Facebook, Instagram, X, YouTube, TikTok. File d’attente intelligente et recyclage automatique.',
  },
  {
    icon: 'users',
    tone: 'sky',
    title: 'CRM & prospection B2B',
    description:
      'Un CRM complet — contacts, entreprises, pipeline, relances — couplé à une prospection B2B multi-sources pour bâtir et enrichir vos listes.',
  },
  {
    icon: 'robot',
    tone: 'emerald',
    title: 'Copilot IA + équipe d’experts 24/7',
    description:
      'Un copilote qui exécute vos actions dans la plateforme, épaulé par une équipe d’agents IA spécialisés qui travaillent pour vous jour et nuit.',
  },
  {
    icon: 'chart',
    tone: 'amber',
    title: 'Analytique & intelligence de compte',
    description:
      'Suivez la performance, le retour sur investissement et l’audience. Des dossiers d’intelligence par compte transforment vos données en prochaines actions.',
  },
  {
    icon: 'megaphone',
    tone: 'rose',
    title: 'Outreach & communauté',
    description:
      'Séquences multicanales, boîte de réception unifiée, gestion de communauté et modération — le tout encadré par un garde-fou anti-pourriel conforme.',
  },
];

/** The dense capability grid — "tout ce que Karexia fait". */
export interface Capability {
  icon: IconKey;
  label: string;
  description: string;
}

export const CAPABILITIES: Capability[] = [
  { icon: 'sparkles', label: 'Studio de contenu IA', description: 'Textes, visuels, carrousels et vidéos générés par IA.' },
  { icon: 'image', label: 'Studio créatif', description: 'Éditeur graphique, gabarits de marque et infographies animées.' },
  { icon: 'calendar', label: 'Calendrier éditorial', description: 'Planification visuelle et publication multi-réseaux.' },
  { icon: 'layers', label: 'File & recyclage', description: 'Recyclage automatique du contenu evergreen et catégories.' },
  { icon: 'globe', label: 'Multi-réseaux', description: 'LinkedIn, Facebook, Instagram, X, YouTube, TikTok.' },
  { icon: 'users', label: 'CRM complet', description: 'Contacts, entreprises, groupes, pipeline et affaires.' },
  { icon: 'search', label: 'Prospection B2B', description: 'Recherche géociblée multi-sources et enrichissement.' },
  { icon: 'target', label: 'Recommandation de prospects', description: 'Priorisation par affinité produit et objectif.' },
  { icon: 'send', label: 'Séquences d’outreach', description: 'Campagnes multicanales : courriel, LinkedIn, SMS, WhatsApp.' },
  { icon: 'inbox', label: 'Boîte unifiée (Unibox)', description: 'Toutes vos conversations entrantes au même endroit.' },
  { icon: 'chat', label: 'Gestion de communauté', description: 'Modération, réponses enregistrées et politiques de SLA.' },
  { icon: 'robot', label: 'Copilot IA', description: 'Un assistant qui agit dans toute la plateforme pour vous.' },
  { icon: 'bolt', label: 'Équipe IA 24/7', description: 'Des agents spécialisés qui exécutent vos mandats en continu.' },
  { icon: 'chart', label: 'Analytique avancée', description: 'Performance, ROI, entonnoirs, cohortes et attribution.' },
  { icon: 'megaphone', label: 'Publicité', description: 'Création et ciblage de campagnes publicitaires.' },
  { icon: 'briefcase', label: 'Marketplace de services', description: 'Répertoire de prestataires, réservations et avis.' },
  { icon: 'rocket', label: 'Lien-en-bio & projets', description: 'Pages bio publiques, projets et flux d’approbation.' },
  { icon: 'shield', label: 'Conformité Loi 25 / LCAP', description: 'Garde-fou anti-pourriel et respect du consentement.' },
];

/** Curated static pricing teaser — the HONEST fallback when the live plans
 * catalogue (GET /api/v1/plans, authentifié) is unavailable to a visitor. */
export interface TeaserPlan {
  code: string;
  name: string;
  priceLabel: string;
  period: string;
  tagline: string;
  features: string[];
  highlighted: boolean;
}

export const TEASER_PLANS: TeaserPlan[] = [
  {
    code: 'starter',
    name: 'Essentiel',
    priceLabel: 'Gratuit',
    period: 'pour commencer',
    tagline: 'Testez la création de contenu IA et la publication sur vos premiers réseaux.',
    features: [
      'Studio de contenu IA (crédits inclus)',
      'Publication sur 3 réseaux',
      'Calendrier éditorial',
      'CRM — jusqu’à 500 contacts',
      'Copilot IA',
    ],
    highlighted: false,
  },
  {
    code: 'pro',
    name: 'Professionnel',
    priceLabel: 'Sur mesure',
    period: 'par mois',
    tagline: 'Pour les équipes qui publient chaque jour et prospectent activement.',
    features: [
      'Crédits IA élargis',
      'Réseaux illimités',
      'Prospection B2B & enrichissement',
      'Séquences d’outreach multicanales',
      'Analytique avancée & ROI',
      'Équipe d’experts IA 24/7',
    ],
    highlighted: true,
  },
  {
    code: 'scale',
    name: 'Entreprise',
    priceLabel: 'Sur mesure',
    period: 'par mois',
    tagline: 'Volume, collaboration et gouvernance pour les organisations exigeantes.',
    features: [
      'Crédits IA sur volume',
      'Collaboration & rôles d’équipe',
      'Gouvernance & pistes d’audit',
      'API développeur & intégrations',
      'Marketplace de services',
      'Accompagnement dédié',
    ],
    highlighted: false,
  },
];

export const PLANS_SECTION = {
  eyebrow: 'Tarification',
  title: 'Un forfait pour chaque étape de votre croissance',
  subtitle:
    'Commencez gratuitement, puis évoluez à votre rythme. Chaque forfait inclut des crédits IA, la publication multi-réseaux et le Copilot.',
  ctaSubscribe: 'Choisir ce forfait',
  ctaLogin: 'Se connecter',
  fallbackNote:
    'Connectez-vous pour voir la tarification en direct et les crédits IA inclus dans votre région.',
  popularBadge: 'Le plus populaire',
  monthly: 'Mensuel',
  annual: 'Annuel',
  annualHint: '2 mois offerts',
} as const;

/** Product screenshots — framed placeholders until a real asset URL is set.
 * Drop a real image here later: set `imageUrl` to a public asset path and the
 * frame renders it in place of the gradient skeleton. */
export interface Screenshot {
  title: string;
  caption: string;
  tone: Tone;
  /** Real image URL — when null, a gradient skeleton mock renders instead. */
  imageUrl: string | null;
}

export const SCREENSHOTS: Screenshot[] = [
  {
    title: 'Tableau de bord',
    caption: 'Votre cockpit : contenu, publications, comptes connectés, crédits IA et prochaines actions.',
    tone: 'indigo',
    imageUrl: null,
  },
  {
    title: 'Studio de contenu IA',
    caption: 'Générez texte, visuels et vidéos, déclinés par réseau, en un clic.',
    tone: 'violet',
    imageUrl: null,
  },
  {
    title: 'Calendrier de publication',
    caption: 'Planifiez et publiez sur tous vos réseaux depuis un seul calendrier.',
    tone: 'sky',
    imageUrl: null,
  },
  {
    title: 'CRM & pipeline',
    caption: 'Suivez contacts, entreprises et affaires, avec relances et rappels automatiques.',
    tone: 'emerald',
    imageUrl: null,
  },
  {
    title: 'Analytique & ROI',
    caption: 'Mesurez la performance, l’audience et le retour sur investissement.',
    tone: 'amber',
    imageUrl: null,
  },
  {
    title: 'Copilot IA',
    caption: 'Demandez, et votre copilote exécute les actions dans la plateforme.',
    tone: 'rose',
    imageUrl: null,
  },
];

export const SCREENSHOTS_SECTION = {
  eyebrow: 'Aperçu du produit',
  title: 'Conçu pour aller vite, pensé pour durer',
  subtitle: 'Un aperçu des espaces de travail que vous utiliserez chaque jour.',
} as const;

/** Demo video — poster + URL both plug in from here. */
export const VIDEO = {
  eyebrow: 'Démonstration',
  title: 'Voir Karexia en action',
  subtitle: 'Deux minutes pour comprendre comment déléguer votre présence en ligne à votre équipe IA.',
  /** Set to a real MP4/embed URL to enable playback. Null → poster + « bientôt ». */
  videoUrl: null as string | null,
  /** Set to a real poster image URL. Null → gradient poster is rendered. */
  posterUrl: null as string | null,
  soonLabel: 'Démo vidéo bientôt disponible',
  playLabel: 'Lire la vidéo',
} as const;

/** Target-persona cards (cibles) — pains → gains. */
export interface Persona {
  icon: IconKey;
  tone: Tone;
  title: string;
  summary: string;
  pains: string[];
  gains: string[];
}

export const PERSONAS: Persona[] = [
  {
    icon: 'megaphone',
    tone: 'indigo',
    title: 'Agences marketing',
    summary: 'Gérez plusieurs clients et marques sans multiplier les outils.',
    pains: ['Jongler entre dix logiciels par client', 'Produire du contenu à grande échelle', 'Prouver le ROI aux clients'],
    gains: ['Contenu et calendrier centralisés par marque', 'Production IA à la chaîne', 'Rapports de performance clairs'],
  },
  {
    icon: 'building',
    tone: 'violet',
    title: 'PME & TPE',
    summary: 'Une présence professionnelle, même sans équipe marketing dédiée.',
    pains: ['Manque de temps et de ressources', 'Difficile d’être régulier', 'Prospection laborieuse'],
    gains: ['L’équipe IA publie et prospecte pour vous', 'Régularité assurée', 'Nouveaux clients sans effort manuel'],
  },
  {
    icon: 'users',
    tone: 'sky',
    title: 'Consultants & créateurs',
    summary: 'Bâtissez votre marque personnelle et convertissez votre audience.',
    pains: ['Créer du contenu en continu', 'Convertir l’audience en clients', 'Rester visible sans y passer ses journées'],
    gains: ['Ghostwriter IA à votre ton', 'Lien-en-bio et capture de prospects', 'Croissance pilotée par la donnée'],
  },
  {
    icon: 'briefcase',
    tone: 'emerald',
    title: 'Cabinets & services professionnels',
    summary: 'Développez votre clientèle en toute conformité.',
    pains: ['Réputation et conformité à protéger', 'Suivi client dispersé', 'Développement d’affaires irrégulier'],
    gains: ['Garde-fou conforme Loi 25 / LCAP', 'CRM et relances structurés', 'Prospection B2B ciblée et mesurable'],
  },
];

export const PERSONAS_SECTION = {
  eyebrow: 'Pour qui',
  title: 'Pensé pour votre réalité',
  subtitle: 'Karexia s’adapte à votre façon de travailler, quelle que soit votre équipe.',
  painsLabel: 'Le défi',
  gainsLabel: 'Avec Karexia',
} as const;

/** Trust / proof band — honest capability claims, not fabricated testimonials. */
export interface ProofStat {
  value: string;
  label: string;
}

export const PROOF_STATS: ProofStat[] = [
  { value: '6', label: 'réseaux sociaux pris en charge' },
  { value: '24/7', label: 'équipe d’experts IA au travail' },
  { value: 'Tout-en-un', label: 'contenu, CRM, prospection, analytique' },
  { value: 'Loi 25', label: 'conçu pour la conformité canadienne' },
];

export const PROOF_SECTION = {
  eyebrow: 'Pourquoi Karexia',
  title: 'Une seule plateforme, à la place de dix outils',
  points: [
    'Remplacez votre pile d’outils marketing par une plateforme unifiée.',
    'Déléguez le travail répétitif à des agents IA, pas à un tableur de plus.',
    'Restez conforme : chaque envoi passe par un garde-fou anti-pourriel.',
  ],
} as const;

/** Closing call-to-action band. */
export const FINAL_CTA = {
  title: 'Prêt à déléguer à votre équipe IA ?',
  subtitle:
    'Connectez-vous pour créer votre premier contenu, planifier vos publications et laisser vos experts IA travailler pour vous.',
  primaryCta: 'Se connecter',
  secondaryCta: 'Créer un compte',
} as const;

/** Footer content. `to` = in-app/public route, `href` = anchor. */
export const FOOTER = {
  tagline: 'La plateforme tout-en-un propulsée par l’IA pour votre présence en ligne.',
  columns: [
    {
      title: 'Produit',
      links: [
        { label: 'Fonctionnalités', href: '#fonctionnalites' },
        { label: 'Capacités', href: '#capacites' },
        { label: 'Aperçu', href: '#apercu' },
        { label: 'Tarifs', href: '#tarifs' },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { label: 'Agences', href: '#cibles' },
        { label: 'PME & TPE', href: '#cibles' },
        { label: 'Consultants', href: '#cibles' },
        { label: 'Services professionnels', href: '#cibles' },
      ],
    },
  ],
  /** Links to real public routes. */
  legalLinks: [
    { label: 'Confidentialité', to: '/confidentialite' },
    { label: 'Politique de confidentialité (EN)', to: '/privacy' },
  ],
  contactEmail: 'info@karexia.com',
  rightsPrefix: 'Tous droits réservés.',
} as const;
